(() => {
  'use strict';

  const MODEL_OPTION_NAMES = [
    'model',
    'car model',
    'vehicle model',
    'vehicle',
    'car'
  ];

  // Maximum size of the optional uploaded image (checked in the browser before upload)
  const MAX_UPLOAD_BYTES = 10 * 1024 * 1024;

  const collator = new Intl.Collator(undefined, {
    numeric: true,
    sensitivity: 'base'
  });

  function normalize(value) {
    return String(value || '').trim().toLowerCase();
  }

  function optionName(option) {
    return typeof option === 'string' ? option : option && option.name;
  }

  function findModelOptionIndex(product) {
    const options = product.options || [];

    const index = options.findIndex((option) =>
      MODEL_OPTION_NAMES.includes(normalize(optionName(option)))
    );

    if (index !== -1) return index;

    if (
      options.length === 1 &&
      normalize(optionName(options[0])) !== 'title'
    ) {
      return 0;
    }

    return -1;
  }

  function el(tag, className, text) {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text !== undefined && text !== null) node.textContent = text;
    return node;
  }

  function sizedImage(src, width) {
    if (!src) return '';
    const url = src.startsWith('//') ? `https:${src}` : src;

    try {
      const parsed = new URL(url, window.location.origin);
      parsed.searchParams.set('width', String(width));
      return parsed.toString();
    } catch (error) {
      return url;
    }
  }

  function imageKey(src) {
    if (!src) return '';
    try {
      return new URL(
        src.startsWith('//') ? `https:${src}` : src,
        window.location.origin
      ).pathname;
    } catch (error) {
      return src;
    }
  }

  // Extracts bullet points from the product description (uses textContent, so no HTML is injected)
  function extractPoints(descriptionHtml) {
    if (!descriptionHtml) return [];

    const spaced = descriptionHtml
      .replace(/<br\s*\/?>/gi, ' ')
      .replace(/<\/(p|div|h[1-6]|li|ul|ol)>/gi, ' $&');

    const doc = new DOMParser().parseFromString(spaced, 'text/html');

    const items = [...doc.querySelectorAll('li')]
      .map((li) => li.textContent.replace(/\s+/g, ' ').trim())
      .filter(Boolean)
      .slice(0, 4);

    if (items.length) return items;

    const paragraph = [...doc.querySelectorAll('p')]
      .map((p) => p.textContent.replace(/\s+/g, ' ').trim())
      .find(Boolean);

    const text = paragraph || doc.body.textContent.trim();

    if (!text) return [];

    return [text.length > 240 ? `${text.slice(0, 237).trim()}...` : text];
  }

  // Splits multi-line text into trimmed, non-empty lines
  function splitLines(value) {
    if (typeof value !== 'string') return [];

    return value
      .split(/\r?\n/)
      .map((line) => line.trim())
      .filter(Boolean);
  }

  // Makes sure description line breaks render as real paragraphs
  // (some themes hide <br> tags, which would merge everything into one paragraph)
  function normalizeDescription(html) {
    const source = String(html || '').trim();

    const hasBlockTags = /<(p|div|h[1-6]|ul|ol|li|table|blockquote)[\s>\/]/i.test(source);

    if (!hasBlockTags) {
      // Plain text or inline-only markup: every line becomes its own paragraph
      return source
        .split(/(?:\r?\n|<br\s*\/?>)+/i)
        .map((line) => line.trim())
        .filter(Boolean)
        .map((line) => `<p>${line}</p>`)
        .join('');
    }

    // Block markup: two or more consecutive <br> tags become a paragraph break
    return source.replace(/(?:<br\s*\/?>\s*){2,}/gi, '</p><p>');
  }

  // Inserts merchant HTML (description, policies) after removing scripts and inline handlers
  function setSafeHtml(container, html) {
    const doc = new DOMParser().parseFromString(html, 'text/html');

    doc
      .querySelectorAll('script, style, iframe, object, embed, link, meta, form')
      .forEach((node) => node.remove());

    doc.body.querySelectorAll('*').forEach((node) => {
      [...node.attributes].forEach((attribute) => {
        const name = attribute.name.toLowerCase();
        const value = attribute.value.trim().toLowerCase();

        if (
          name.startsWith('on') ||
          ((name === 'href' || name === 'src' || name === 'xlink:href') &&
            value.startsWith('javascript:'))
        ) {
          node.removeAttribute(attribute.name);
        }
      });
    });

    container.replaceChildren(...doc.body.childNodes);
  }

  // Fullscreen image preview (lightbox)
  function openLightbox(images, startIndex, altText, returnFocusTo) {
    let index = startIndex;
    const previousOverflow = document.body.style.overflow;

    const overlay = el('div', 'vf-lightbox');
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-modal', 'true');
    overlay.setAttribute('aria-label', 'Image preview');

    const stage = el('div', 'vf-lightbox__stage');
    const img = document.createElement('img');
    img.className = 'vf-lightbox__image';
    img.alt = altText || '';
    stage.append(img);

    const closeButton = el('button', 'vf-lightbox__btn vf-lightbox__close', '×');
    closeButton.type = 'button';
    closeButton.setAttribute('aria-label', 'Close preview');

    const counter = el('span', 'vf-lightbox__counter');
    const controls = [closeButton];

    overlay.append(stage, closeButton);

    function show(next) {
      index = (next + images.length) % images.length;
      img.src = sizedImage(images[index], 1800);
      counter.textContent = `${index + 1} / ${images.length}`;
    }

    function close() {
      document.removeEventListener('keydown', onKeydown);
      overlay.remove();
      document.body.style.overflow = previousOverflow;
      if (returnFocusTo && returnFocusTo.focus) returnFocusTo.focus();
    }

    function onKeydown(event) {
      if (event.key === 'Escape') {
        close();
      } else if (event.key === 'ArrowLeft' && images.length > 1) {
        show(index - 1);
      } else if (event.key === 'ArrowRight' && images.length > 1) {
        show(index + 1);
      } else if (event.key === 'Tab') {
        // Keep keyboard focus inside the lightbox
        const first = controls[0];
        const last = controls[controls.length - 1];

        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }
    }

    if (images.length > 1) {
      const prev = el('button', 'vf-lightbox__btn vf-lightbox__nav vf-lightbox__nav--prev', '‹');
      const next = el('button', 'vf-lightbox__btn vf-lightbox__nav vf-lightbox__nav--next', '›');

      prev.type = 'button';
      next.type = 'button';
      prev.setAttribute('aria-label', 'Previous image');
      next.setAttribute('aria-label', 'Next image');

      prev.addEventListener('click', () => show(index - 1));
      next.addEventListener('click', () => show(index + 1));

      overlay.append(prev, next, counter);
      controls.push(prev, next);

      let touchStartX = null;

      stage.addEventListener('touchstart', (event) => {
        touchStartX = event.changedTouches[0].clientX;
      }, { passive: true });

      stage.addEventListener('touchend', (event) => {
        if (touchStartX === null) return;
        const deltaX = event.changedTouches[0].clientX - touchStartX;
        touchStartX = null;

        if (Math.abs(deltaX) > 50) show(deltaX > 0 ? index - 1 : index + 1);
      }, { passive: true });
    }

    closeButton.addEventListener('click', close);

    // Close when the backdrop is clicked (not when the image is clicked)
    overlay.addEventListener('click', (event) => {
      if (event.target === overlay || event.target === stage) close();
    });

    document.addEventListener('keydown', onKeydown);
    document.body.style.overflow = 'hidden';
    document.body.append(overlay);

    show(index);
    closeButton.focus();
  }

  function initializeFinder(section) {
    if (section.dataset.finderInitialized === 'true') return;
    section.dataset.finderInitialized = 'true';

    const brandSelect = section.querySelector('[data-brand-select]');
    const modelSelect = section.querySelector('[data-model-select]');
    const trimInput = section.querySelector('[data-trim-input]');
    const yearInput = section.querySelector('[data-year-input]');
    const form = section.querySelector('[data-vehicle-finder-form]');
    const message = section.querySelector('[data-finder-message]');
    const results = section.querySelector('[data-finder-results]');
    const resultsContent = section.querySelector(
      '[data-finder-results-content]'
    );

    if (!brandSelect || !modelSelect || !form) return;

    let config = {};

    try {
      const configElement = section.querySelector('[data-finder-config]');
      config = configElement ? JSON.parse(configElement.textContent) : {};
    } catch (error) {
      console.warn('[vehicle-finder] Invalid config JSON', error);
    }

    const root = config.root || '/';
    const cartUrl = config.cart_url || `${root}cart`;
    const checkoutUrl = config.checkout_url || `${root}checkout`;
    const addLabel = config.add_label || 'Add To Cart';
    const buyLabel = config.buy_label || 'Buy Now';
    const trustPoints = (config.trust || [])
      .map((item) => String(item).trim())
      .filter(Boolean);
    const showDetails = config.show_details !== false;
    const uploadEnabled = config.enable_upload !== false;
    const uploadLabel =
      String(config.upload_label || '').trim() || 'Dashboard Image';
    const shippingPolicy = config.shipping_policy || '';

    let products = [];
    let selectedProduct = null;
    let modelIndex = -1;
    let requestId = 0;

    function setMessage(text) {
      if (!message) return;
      message.textContent = text || '';
      message.hidden = !text;
    }

    function clearResults() {
      requestId += 1;
      if (results) results.hidden = true;
      if (resultsContent) resultsContent.replaceChildren();
    }

    function resetModels() {
      modelSelect.replaceChildren(new Option('Select Model', ''));
      modelSelect.disabled = true;
      modelIndex = -1;
    }

    function renderModels(product) {
      resetModels();
      if (!product) return;

      modelIndex = findModelOptionIndex(product);

      if (modelIndex === -1) {
        setMessage(
          'Model variant option was not found for this product. ' +
          'Please check the product variant option names.'
        );
        return;
      }

      const modelMap = new Map();

      (product.variants || []).forEach((variant) => {
        const model = variant.options && variant.options[modelIndex];
        if (!model) return;
        modelMap.set(
          model,
          Boolean(modelMap.get(model)) || Boolean(variant.available)
        );
      });

      const models = [...modelMap.keys()].sort(collator.compare);

      models.forEach((model) => {
        const inStock = modelMap.get(model);
        const option = new Option(
          inStock ? model : `${model} (Sold out)`,
          model
        );
        option.disabled = !inStock;
        modelSelect.add(option);
      });

      modelSelect.disabled = models.length === 0;

      setMessage(
        models.length === 0 ? 'No models are available for this brand.' : ''
      );
    }

    function pickVariant(product, model) {
      const matches = (product.variants || []).filter(
        (variant) => variant.options && variant.options[modelIndex] === model
      );

      return matches.find((variant) => variant.available) || matches[0] || null;
    }

    async function fetchProductDetail(product) {
      try {
        const response = await fetch(
          `${root}products/${encodeURIComponent(product.handle)}.js`,
          { headers: { Accept: 'application/json' } }
        );

        if (!response.ok) return null;

        return await response.json();
      } catch (error) {
        console.warn('[vehicle-finder] Product details fetch failed', error);
        return null;
      }
    }

    // Adds one item to the cart. When an image is attached, the request is sent as
    // multipart form data so Shopify stores the file and saves its URL as a line item property.
    async function addToCart(variantId, quantity, properties, file) {
      let response;

      if (file) {
        const formData = new FormData();
        formData.append('id', String(variantId));
        formData.append('quantity', String(quantity));

        Object.entries(properties).forEach(([key, value]) => {
          formData.append(`properties[${key}]`, value);
        });

        formData.append(`properties[${uploadLabel}]`, file, file.name);

        response = await fetch(`${root}cart/add.js`, {
          method: 'POST',
          headers: { Accept: 'application/json' },
          body: formData
        });
      } else {
        const item = { id: variantId, quantity };

        if (Object.keys(properties).length) item.properties = properties;

        response = await fetch(`${root}cart/add.js`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json'
          },
          body: JSON.stringify({ items: [item] })
        });
      }

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(
          data.description ||
          data.message ||
          'Could not add this product to the cart.'
        );
      }

      return data;
    }

    // Clones the static icon boxes rendered by Liquid (section blocks)
    function buildIconBox() {
      const template = section.querySelector('template[data-finder-icon-box]');
      if (!template) return null;

      const fragment = template.content.cloneNode(true);

      // Safety: never run scripts that might be pasted into a custom SVG field
      fragment.querySelectorAll('script').forEach((node) => node.remove());

      return fragment;
    }

    // Builds the Description / Key Features / FAQs / Shipping & Returns accordion
    function buildAccordion(product, detail) {
      const rows = [];

      const description =
        detail && typeof detail.description === 'string'
          ? detail.description.trim()
          : '';

      if (description) {
        const body = el('div', 'vf-acc__rich');
        setSafeHtml(body, normalizeDescription(description));
        rows.push({ title: 'DESCRIPTION', body, open: true });
      }

      const features = splitLines(product.key_features);

      if (features.length) {
        const list = el('ul', 'vf-acc__features');
        features.forEach((line) => list.append(el('li', null, line)));
        rows.push({ title: 'KEY FEATURES', body: list, open: false });
      }

      const faqLines = splitLines(product.faqs);

      if (faqLines.length) {
        const body = el('div', 'vf-acc__text');

        faqLines.forEach((line, index) => {
          if (index > 0) body.append(document.createElement('br'));
          body.append(document.createTextNode(line));
        });

        rows.push({ title: 'FAQs', body, open: false });
      }

      const shippingLines = splitLines(product.shipping_returns);

      if (shippingLines.length) {
        const body = el('div', 'vf-acc__rich');
        shippingLines.forEach((line) => body.append(el('p', null, line)));
        rows.push({ title: 'SHIPPING & RETURNS', body, open: false });
      } else if (shippingPolicy.trim()) {
        const body = el('div', 'vf-acc__rich');
        setSafeHtml(body, shippingPolicy);
        rows.push({ title: 'SHIPPING & RETURNS', body, open: false });
      }

      if (!rows.length) return null;

      const accordion = el('div', 'vf-acc');

      rows.forEach((row, index) => {
        const bodyId = `${section.id}-acc-${index}`;
        const rowElement = el(
          'div',
          row.open ? 'vf-acc__row is-open' : 'vf-acc__row'
        );

        const header = el('button', 'vf-acc__header');
        header.type = 'button';
        header.setAttribute('aria-expanded', String(row.open));
        header.setAttribute('aria-controls', bodyId);
        header.append(
          el('span', null, row.title),
          el('span', 'vf-acc__toggle', '+')
        );

        const bodyWrap = el('div', 'vf-acc__body');
        bodyWrap.id = bodyId;
        bodyWrap.inert = !row.open;

        const inner = el('div', 'vf-acc__inner');
        const content = el('div', 'vf-acc__content');
        content.append(row.body);
        inner.append(content);
        bodyWrap.append(inner);

        header.addEventListener('click', () => {
          const isOpen = rowElement.classList.toggle('is-open');
          header.setAttribute('aria-expanded', String(isOpen));
          bodyWrap.inert = !isOpen;
        });

        rowElement.append(header, bodyWrap);
        accordion.append(rowElement);
      });

      return accordion;
    }

    function renderDetail(product, variant, detail, trim, year) {
      const uid = `${section.id}-detail`;
      const modelName = modelSelect.value;
      const title = (detail && detail.title) || product.title;

      const wrap = el('div', 'vf-detail');

      /* ----- Gallery ----- */
      let images = ((detail && detail.images) || []).slice();

      const detailVariant =
        detail &&
        (detail.variants || []).find((item) => item.id === variant.id);

      const variantImage =
        (detailVariant &&
          detailVariant.featured_image &&
          detailVariant.featured_image.src) ||
        variant.image;

      if (variantImage) {
        const key = imageKey(variantImage);
        images = [
          variantImage,
          ...images.filter((src) => imageKey(src) !== key)
        ];
      }

      if (!images.length && product.image) images = [product.image];

      const gallery = el('div', 'vf-gallery');
      const thumbs = el('div', 'vf-thumbs');
      const stage = el('div', 'vf-stage');
      const mainImage = document.createElement('img');
      const thumbButtons = [];
      let current = 0;

      mainImage.className = 'vf-stage__image';
      mainImage.alt = `${title} ${modelName}`;
      mainImage.width = 900;
      mainImage.height = 900;

      function showImage(index) {
        if (!images.length) return;
        current = (index + images.length) % images.length;
        mainImage.src = sizedImage(images[current], 900);
        thumbButtons.forEach((button, i) => {
          button.classList.toggle('is-active', i === current);
          button.setAttribute('aria-current', i === current ? 'true' : 'false');
        });
      }

      if (images.length) {
        stage.append(mainImage);
      } else {
        stage.classList.add('vf-stage--empty');
      }

      if (images.length > 1) {
        images.forEach((src, index) => {
          const button = el('button', 'vf-thumb');
          button.type = 'button';
          button.setAttribute('aria-label', `Show image ${index + 1}`);

          const img = document.createElement('img');
          img.src = sizedImage(src, 160);
          img.alt = '';
          img.loading = 'lazy';
          button.append(img);

          button.addEventListener('click', () => showImage(index));
          thumbs.append(button);
          thumbButtons.push(button);
        });

        const prev = el('button', 'vf-arrow vf-arrow--prev', '‹');
        const next = el('button', 'vf-arrow vf-arrow--next', '›');

        prev.type = 'button';
        next.type = 'button';
        prev.setAttribute('aria-label', 'Previous image');
        next.setAttribute('aria-label', 'Next image');

        prev.addEventListener('click', () => showImage(current - 1));
        next.addEventListener('click', () => showImage(current + 1));

        stage.append(prev, next);
        gallery.append(thumbs);
      }

      gallery.append(stage);
      showImage(0);

      if (images.length) {
        const openPreview = () =>
          openLightbox(images, current, mainImage.alt, mainImage);

        mainImage.classList.add('is-zoomable');
        mainImage.tabIndex = 0;
        mainImage.setAttribute('role', 'button');
        mainImage.setAttribute('aria-label', 'Open image preview');

        mainImage.addEventListener('click', openPreview);
        mainImage.addEventListener('keydown', (event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            openPreview();
          }
        });
      }

      /* ----- Info ----- */
      const info = el('div', 'vf-info');

      const titleHasModel = normalize(title).endsWith(normalize(modelName));
      info.append(
        el('h4', 'vf-title', titleHasModel ? title : `${title} ${modelName}`.trim())
      );

      const priceRow = el('div', 'vf-price-row');
      priceRow.append(el('span', 'vf-price', variant.price));

      if (variant.compare_at) {
        priceRow.append(el('span', 'vf-compare', variant.compare_at));

        if (variant.compare_at_cents > variant.price_cents) {
          const percent = Math.round(
            ((variant.compare_at_cents - variant.price_cents) /
              variant.compare_at_cents) *
              100
          );
          priceRow.append(el('span', 'vf-badge', `${percent}% OFF`));
        }
      }

      info.append(priceRow);

      const points = extractPoints(detail && detail.description);

      if (points.length) {
        const list = el('ul', 'vf-points');
        points.forEach((point) => list.append(el('li', null, point)));
        info.append(list);
      }

      if (trustPoints.length) {
        const trust = el('ul', 'vf-trust');
        trustPoints.forEach((point) => trust.append(el('li', null, point)));
        info.append(trust);
      }

      /* ----- Two input boxes (Trim, Year) ----- */
      const fields = el('div', 'vf-fields');

      function buildField(labelText, id, attrs) {
        const field = el('div', 'vehicle-finder__field');
        const label = el('label', null, `${labelText} `);
        label.htmlFor = id;

        // Required marker, same style as the Brand and Model labels
        const marker = el('span', null, '*');
        marker.setAttribute('aria-hidden', 'true');
        label.append(marker);

        const input = document.createElement('input');
        input.id = id;
        input.required = true;
        input.setAttribute('aria-required', 'true');
        Object.entries(attrs).forEach(([key, value]) =>
          input.setAttribute(key, value)
        );

        // Remove the error highlight as soon as the user starts typing
        input.addEventListener('input', () => {
          input.removeAttribute('aria-invalid');
        });

        field.append(label, input);
        return { field, input };
      }

      const trimField = buildField('Trim', `${uid}-trim`, {
        type: 'text',
        placeholder: 'Enter Trim',
        autocomplete: 'off'
      });

      const yearField = buildField('Year', `${uid}-year`, {
        type: 'number',
        inputmode: 'numeric',
        min: '1900',
        max: '2100',
        step: '1',
        placeholder: 'Enter Year'
      });

      trimField.input.value = trim || '';
      yearField.input.value = year || '';

      fields.append(trimField.field, yearField.field);

      /* ----- Optional image upload ----- */
      let selectedFile = null;
      let previewUrl = '';

      if (uploadEnabled) {
        const uploadId = `${uid}-upload`;
        const uploadField = el('div', 'vehicle-finder__field vf-upload');

        const uploadLabelElement = el('label', null, `${uploadLabel} `);
        uploadLabelElement.htmlFor = uploadId;
        uploadLabelElement.append(el('span', 'vf-upload__optional', '(optional)'));

        const fileInput = document.createElement('input');
        fileInput.id = uploadId;
        fileInput.type = 'file';
        fileInput.accept = 'image/*';
        fileInput.className = 'vf-upload__input';

        const hint = el(
          'p',
          'vf-upload__hint',
          'Image files only (JPG, PNG or WEBP), up to 10 MB.'
        );

        const preview = el('div', 'vf-upload__preview');
        preview.hidden = true;

        const thumb = document.createElement('img');
        thumb.className = 'vf-upload__thumb';
        thumb.alt = '';

        const fileName = el('span', 'vf-upload__name');
        const removeButton = el('button', 'vf-upload__remove', 'Remove');
        removeButton.type = 'button';
        removeButton.setAttribute('aria-label', 'Remove the selected image');

        preview.append(thumb, fileName, removeButton);

        // Resets the selected file and its preview
        function clearFile() {
          selectedFile = null;

          if (previewUrl) {
            URL.revokeObjectURL(previewUrl);
            previewUrl = '';
          }

          fileInput.value = '';
          preview.hidden = true;
          thumb.removeAttribute('src');
          fileName.textContent = '';
        }

        fileInput.addEventListener('change', () => {
          const file = fileInput.files && fileInput.files[0];

          clearFile();

          if (!file) return;

          if (!file.type || !file.type.startsWith('image/')) {
            setStatus('Please choose an image file (JPG, PNG or WEBP).', 'error');
            return;
          }

          if (file.size > MAX_UPLOAD_BYTES) {
            setStatus('The image is too large. The maximum size is 10 MB.', 'error');
            return;
          }

          setStatus('');

          selectedFile = file;
          previewUrl = URL.createObjectURL(file);
          thumb.src = previewUrl;
          fileName.textContent = file.name;
          preview.hidden = false;
        });

        removeButton.addEventListener('click', clearFile);

        uploadField.append(uploadLabelElement, fileInput, hint, preview);
        fields.append(uploadField);
      }

      info.append(fields);

      /* ----- Quantity + buttons ----- */
      const actions = el('div', 'vf-actions');

      const qty = el('div', 'vf-qty');
      const minus = el('button', 'vf-qty__btn', '−');
      const plus = el('button', 'vf-qty__btn', '+');
      const qtyInput = document.createElement('input');

      minus.type = 'button';
      plus.type = 'button';
      minus.setAttribute('aria-label', 'Decrease quantity');
      plus.setAttribute('aria-label', 'Increase quantity');

      qtyInput.type = 'number';
      qtyInput.min = '1';
      qtyInput.step = '1';
      qtyInput.value = '1';
      qtyInput.inputMode = 'numeric';
      qtyInput.setAttribute('aria-label', 'Quantity');

      function getQuantity() {
        const value = parseInt(qtyInput.value, 10);
        return Number.isInteger(value) && value > 0 ? value : 1;
      }

      minus.addEventListener('click', () => {
        qtyInput.value = String(Math.max(1, getQuantity() - 1));
      });

      plus.addEventListener('click', () => {
        qtyInput.value = String(getQuantity() + 1);
      });

      qtyInput.addEventListener('change', () => {
        qtyInput.value = String(getQuantity());
      });

      qty.append(minus, qtyInput, plus);

      const addButton = el('button', 'vf-btn vf-btn--add', addLabel);
      const buyButton = el('button', 'vf-btn vf-btn--buy', buyLabel);

      addButton.type = 'button';
      buyButton.type = 'button';

      if (!variant.available) {
        addButton.textContent = 'Sold out';
        addButton.disabled = true;
        buyButton.disabled = true;
        qtyInput.disabled = true;
        minus.disabled = true;
        plus.disabled = true;
      }

      actions.append(qty, addButton, buyButton);
      info.append(actions);

      const status = el('p', 'vf-status');
      status.setAttribute('role', 'status');
      status.setAttribute('aria-live', 'polite');
      status.hidden = true;
      info.append(status);

      // const fullLink = el('a', 'vf-full-link', 'View full product page');
      // fullLink.href = variant.url || `${product.url}?variant=${variant.id}`;
      // info.append(fullLink);

      function setStatus(text, type) {
        status.textContent = '';
        status.className = 'vf-status';
        status.hidden = !text;

        if (!text) return;

        if (type) status.classList.add(`vf-status--${type}`);
        status.append(document.createTextNode(text));

        if (type === 'success') {
          const link = el('a', 'vf-status__link', 'View cart');
          link.href = cartUrl;
          status.append(' ', link);
        }
      }

      // Trim and Year are mandatory before adding to cart or buying
      function collectProperties() {
        const trimValue = trimField.input.value.trim();
        const yearValue = yearField.input.value.trim();

        trimField.input.removeAttribute('aria-invalid');
        yearField.input.removeAttribute('aria-invalid');

        const missing = [];

        if (!trimValue) {
          missing.push('Trim');
          trimField.input.setAttribute('aria-invalid', 'true');
        }

        if (!yearValue) {
          missing.push('Year');
          yearField.input.setAttribute('aria-invalid', 'true');
        }

        if (missing.length) {
          setStatus(
            `Please enter ${missing.join(' and ')} before continuing.`,
            'error'
          );

          (trimValue ? yearField : trimField).input.focus();
          return null;
        }

        const number = Number(yearValue);

        if (!Number.isInteger(number) || number < 1900 || number > 2100) {
          yearField.input.setAttribute('aria-invalid', 'true');
          setStatus('Please enter a valid year between 1900 and 2100.', 'error');
          yearField.input.focus();
          return null;
        }

        return { Trim: trimValue, Year: String(number) };
      }

      let busy = false;

      function setBusy(value) {
        busy = value;
        addButton.disabled = value || !variant.available;
        buyButton.disabled = value || !variant.available;
      }

      addButton.addEventListener('click', async () => {
        if (busy) return;

        const properties = collectProperties();
        if (!properties) return;

        setBusy(true);
        setStatus('');

        try {
          await addToCart(variant.id, getQuantity(), properties, selectedFile);
          setStatus('Added to your cart.', 'success');

          // Generic events so a theme cart drawer / cart count can refresh
          ['cart:refresh', 'cart:updated'].forEach((name) =>
            document.dispatchEvent(new CustomEvent(name, { bubbles: true }))
          );
        } catch (error) {
          setStatus(error.message, 'error');
        } finally {
          setBusy(false);
        }
      });

      buyButton.addEventListener('click', async () => {
        if (busy) return;

        const properties = collectProperties();
        if (!properties) return;

        setBusy(true);
        setStatus('');

        try {
          await addToCart(variant.id, getQuantity(), properties, selectedFile);
          window.location.href = checkoutUrl;
        } catch (error) {
          setStatus(error.message, 'error');
          setBusy(false);
        }
      });

      wrap.append(gallery, info);

      const fragment = document.createDocumentFragment();
      fragment.append(wrap);

      const iconBox = buildIconBox();
      if (iconBox) fragment.append(iconBox);

      const accordion = showDetails ? buildAccordion(product, detail) : null;
      if (accordion) fragment.append(accordion);

      resultsContent.replaceChildren(fragment);
      results.hidden = false;
    }

    async function showProduct(product, variant, trim, year) {
      const currentRequest = ++requestId;

      results.hidden = false;
      resultsContent.replaceChildren(
        el('p', 'vf-loading', 'Loading product details...')
      );

      const detail = await fetchProductDetail(product);

      // Skip rendering if the user searched or changed a selection in the meantime
      if (currentRequest !== requestId) return;

      renderDetail(product, variant, detail, trim, year);
      results.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }

    function initialize() {
      const productsElement = section.querySelector('[data-finder-products]');

      if (!productsElement) {
        throw new Error('Product data script was not found.');
      }

      products = JSON.parse(productsElement.textContent);

      brandSelect.replaceChildren(new Option('Select Brand', ''));

      products
        .slice()
        .sort((a, b) => collator.compare(a.title, b.title))
        .forEach((product) => {
          brandSelect.add(new Option(product.title, String(product.id)));
        });

      brandSelect.disabled = products.length === 0;

      setMessage(
        products.length === 0
          ? 'No products were found in this collection. ' +
            'Select a collection in the section settings if this is not a collection page.'
          : ''
      );
    }

    brandSelect.addEventListener('change', () => {
      const productId = String(brandSelect.value).trim();

      clearResults();

      selectedProduct =
        products.find((product) => String(product.id) === productId) || null;

      if (!selectedProduct) {
        resetModels();
        setMessage('');
        return;
      }

      setMessage('');
      renderModels(selectedProduct);
    });

    // Validates the current selection and shows the matching product
    function runSearch() {
      if (!selectedProduct || !modelSelect.value) {
        clearResults();
        setMessage('Please select both Brand and Model.');
        return;
      }

      const trim = trimInput ? trimInput.value.trim() : '';
      const yearRaw = yearInput ? yearInput.value.trim() : '';

      if (yearRaw) {
        const year = Number(yearRaw);

        if (!Number.isInteger(year) || year < 1900 || year > 2100) {
          clearResults();
          setMessage('Please enter a valid year between 1900 and 2100.');
          return;
        }
      }

      const variant = pickVariant(selectedProduct, modelSelect.value);

      if (!variant) {
        clearResults();
        setMessage('No product found for this model. Please try another one.');
        return;
      }

      setMessage('');
      showProduct(selectedProduct, variant, trim, yearRaw);
    }

    // The product details appear automatically as soon as a model is selected
    modelSelect.addEventListener('change', () => {
      clearResults();
      setMessage('');

      if (modelSelect.value) runSearch();
    });

    // The search button still works as a manual fallback
    form.addEventListener('submit', (event) => {
      event.preventDefault();
      runSearch();
    });

    try {
      initialize();
    } catch (error) {
      console.error('[vehicle-finder]', error);
      brandSelect.replaceChildren(new Option('Unable to load brands', ''));
      brandSelect.disabled = true;
      setMessage('Products could not be loaded. Please refresh and try again.');
    }
  }

  function initializeAllFinders(root = document) {
    root.querySelectorAll('[data-vehicle-finder]').forEach(initializeFinder);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initializeAllFinders());
  } else {
    initializeAllFinders();
  }

  document.addEventListener('shopify:section:load', (event) => {
    if (event.target && event.target.querySelectorAll) {
      initializeAllFinders(event.target);
    }
  });
})();


//to display single image

// (() => {
//   'use strict';

//   const MODEL_OPTION_NAMES = [
//     'model',
//     'car model',
//     'vehicle model',
//     'vehicle',
//     'car'
//   ];

//   // Maximum size of the optional uploaded image (checked in the browser before upload)
//   const MAX_UPLOAD_BYTES = 10 * 1024 * 1024;

//   const collator = new Intl.Collator(undefined, {
//     numeric: true,
//     sensitivity: 'base'
//   });

//   function normalize(value) {
//     return String(value || '').trim().toLowerCase();
//   }

//   function optionName(option) {
//     return typeof option === 'string' ? option : option && option.name;
//   }

//   function findModelOptionIndex(product) {
//     const options = product.options || [];

//     const index = options.findIndex((option) =>
//       MODEL_OPTION_NAMES.includes(normalize(optionName(option)))
//     );

//     if (index !== -1) return index;

//     if (
//       options.length === 1 &&
//       normalize(optionName(options[0])) !== 'title'
//     ) {
//       return 0;
//     }

//     return -1;
//   }

//   function el(tag, className, text) {
//     const node = document.createElement(tag);
//     if (className) node.className = className;
//     if (text !== undefined && text !== null) node.textContent = text;
//     return node;
//   }

//   function sizedImage(src, width) {
//     if (!src) return '';
//     const url = src.startsWith('//') ? `https:${src}` : src;

//     try {
//       const parsed = new URL(url, window.location.origin);
//       parsed.searchParams.set('width', String(width));
//       return parsed.toString();
//     } catch (error) {
//       return url;
//     }
//   }

//   // Extracts bullet points from the product description (uses textContent, so no HTML is injected)
//   function extractPoints(descriptionHtml) {
//     if (!descriptionHtml) return [];

//     const spaced = descriptionHtml
//       .replace(/<br\s*\/?>/gi, ' ')
//       .replace(/<\/(p|div|h[1-6]|li|ul|ol)>/gi, ' $&');

//     const doc = new DOMParser().parseFromString(spaced, 'text/html');

//     const items = [...doc.querySelectorAll('li')]
//       .map((li) => li.textContent.replace(/\s+/g, ' ').trim())
//       .filter(Boolean)
//       .slice(0, 4);

//     if (items.length) return items;

//     const paragraph = [...doc.querySelectorAll('p')]
//       .map((p) => p.textContent.replace(/\s+/g, ' ').trim())
//       .find(Boolean);

//     const text = paragraph || doc.body.textContent.trim();

//     if (!text) return [];

//     return [text.length > 240 ? `${text.slice(0, 237).trim()}...` : text];
//   }

//   // Splits multi-line text into trimmed, non-empty lines
//   function splitLines(value) {
//     if (typeof value !== 'string') return [];

//     return value
//       .split(/\r?\n/)
//       .map((line) => line.trim())
//       .filter(Boolean);
//   }

//   // Makes sure description line breaks render as real paragraphs
//   // (some themes hide <br> tags, which would merge everything into one paragraph)
//   function normalizeDescription(html) {
//     const source = String(html || '').trim();

//     const hasBlockTags = /<(p|div|h[1-6]|ul|ol|li|table|blockquote)[\s>\/]/i.test(source);

//     if (!hasBlockTags) {
//       // Plain text or inline-only markup: every line becomes its own paragraph
//       return source
//         .split(/(?:\r?\n|<br\s*\/?>)+/i)
//         .map((line) => line.trim())
//         .filter(Boolean)
//         .map((line) => `<p>${line}</p>`)
//         .join('');
//     }

//     // Block markup: two or more consecutive <br> tags become a paragraph break
//     return source.replace(/(?:<br\s*\/?>\s*){2,}/gi, '</p><p>');
//   }

//   // Inserts merchant HTML (description, policies) after removing scripts and inline handlers
//   function setSafeHtml(container, html) {
//     const doc = new DOMParser().parseFromString(html, 'text/html');

//     doc
//       .querySelectorAll('script, style, iframe, object, embed, link, meta, form')
//       .forEach((node) => node.remove());

//     doc.body.querySelectorAll('*').forEach((node) => {
//       [...node.attributes].forEach((attribute) => {
//         const name = attribute.name.toLowerCase();
//         const value = attribute.value.trim().toLowerCase();

//         if (
//           name.startsWith('on') ||
//           ((name === 'href' || name === 'src' || name === 'xlink:href') &&
//             value.startsWith('javascript:'))
//         ) {
//           node.removeAttribute(attribute.name);
//         }
//       });
//     });

//     container.replaceChildren(...doc.body.childNodes);
//   }

//   // Fullscreen image preview (lightbox)
//   function openLightbox(images, startIndex, altText, returnFocusTo) {
//     let index = startIndex;
//     const previousOverflow = document.body.style.overflow;

//     const overlay = el('div', 'vf-lightbox');
//     overlay.setAttribute('role', 'dialog');
//     overlay.setAttribute('aria-modal', 'true');
//     overlay.setAttribute('aria-label', 'Image preview');

//     const stage = el('div', 'vf-lightbox__stage');
//     const img = document.createElement('img');
//     img.className = 'vf-lightbox__image';
//     img.alt = altText || '';
//     stage.append(img);

//     const closeButton = el('button', 'vf-lightbox__btn vf-lightbox__close', '×');
//     closeButton.type = 'button';
//     closeButton.setAttribute('aria-label', 'Close preview');

//     const counter = el('span', 'vf-lightbox__counter');
//     const controls = [closeButton];

//     overlay.append(stage, closeButton);

//     function show(next) {
//       index = (next + images.length) % images.length;
//       img.src = sizedImage(images[index], 1800);
//       counter.textContent = `${index + 1} / ${images.length}`;
//     }

//     function close() {
//       document.removeEventListener('keydown', onKeydown);
//       overlay.remove();
//       document.body.style.overflow = previousOverflow;
//       if (returnFocusTo && returnFocusTo.focus) returnFocusTo.focus();
//     }

//     function onKeydown(event) {
//       if (event.key === 'Escape') {
//         close();
//       } else if (event.key === 'ArrowLeft' && images.length > 1) {
//         show(index - 1);
//       } else if (event.key === 'ArrowRight' && images.length > 1) {
//         show(index + 1);
//       } else if (event.key === 'Tab') {
//         // Keep keyboard focus inside the lightbox
//         const first = controls[0];
//         const last = controls[controls.length - 1];

//         if (event.shiftKey && document.activeElement === first) {
//           event.preventDefault();
//           last.focus();
//         } else if (!event.shiftKey && document.activeElement === last) {
//           event.preventDefault();
//           first.focus();
//         }
//       }
//     }

//     if (images.length > 1) {
//       const prev = el('button', 'vf-lightbox__btn vf-lightbox__nav vf-lightbox__nav--prev', '‹');
//       const next = el('button', 'vf-lightbox__btn vf-lightbox__nav vf-lightbox__nav--next', '›');

//       prev.type = 'button';
//       next.type = 'button';
//       prev.setAttribute('aria-label', 'Previous image');
//       next.setAttribute('aria-label', 'Next image');

//       prev.addEventListener('click', () => show(index - 1));
//       next.addEventListener('click', () => show(index + 1));

//       overlay.append(prev, next, counter);
//       controls.push(prev, next);

//       let touchStartX = null;

//       stage.addEventListener('touchstart', (event) => {
//         touchStartX = event.changedTouches[0].clientX;
//       }, { passive: true });

//       stage.addEventListener('touchend', (event) => {
//         if (touchStartX === null) return;
//         const deltaX = event.changedTouches[0].clientX - touchStartX;
//         touchStartX = null;

//         if (Math.abs(deltaX) > 50) show(deltaX > 0 ? index - 1 : index + 1);
//       }, { passive: true });
//     }

//     closeButton.addEventListener('click', close);

//     // Close when the backdrop is clicked (not when the image is clicked)
//     overlay.addEventListener('click', (event) => {
//       if (event.target === overlay || event.target === stage) close();
//     });

//     document.addEventListener('keydown', onKeydown);
//     document.body.style.overflow = 'hidden';
//     document.body.append(overlay);

//     show(index);
//     closeButton.focus();
//   }

//   function initializeFinder(section) {
//     if (section.dataset.finderInitialized === 'true') return;
//     section.dataset.finderInitialized = 'true';

//     const brandSelect = section.querySelector('[data-brand-select]');
//     const modelSelect = section.querySelector('[data-model-select]');
//     const trimInput = section.querySelector('[data-trim-input]');
//     const yearInput = section.querySelector('[data-year-input]');
//     const form = section.querySelector('[data-vehicle-finder-form]');
//     const message = section.querySelector('[data-finder-message]');
//     const results = section.querySelector('[data-finder-results]');
//     const resultsContent = section.querySelector(
//       '[data-finder-results-content]'
//     );

//     if (!brandSelect || !modelSelect || !form) return;

//     let config = {};

//     try {
//       const configElement = section.querySelector('[data-finder-config]');
//       config = configElement ? JSON.parse(configElement.textContent) : {};
//     } catch (error) {
//       console.warn('[vehicle-finder] Invalid config JSON', error);
//     }

//     const root = config.root || '/';
//     const cartUrl = config.cart_url || `${root}cart`;
//     const checkoutUrl = config.checkout_url || `${root}checkout`;
//     const addLabel = config.add_label || 'Add To Cart';
//     const buyLabel = config.buy_label || 'Buy Now';
//     const trustPoints = (config.trust || [])
//       .map((item) => String(item).trim())
//       .filter(Boolean);
//     const showDetails = config.show_details !== false;
//     const uploadEnabled = config.enable_upload !== false;
//     const uploadLabel =
//       String(config.upload_label || '').trim() || 'Dashboard Image';
//     const shippingPolicy = config.shipping_policy || '';

//     let products = [];
//     let selectedProduct = null;
//     let modelIndex = -1;
//     let requestId = 0;

//     function setMessage(text) {
//       if (!message) return;
//       message.textContent = text || '';
//       message.hidden = !text;
//     }

//     function clearResults() {
//       requestId += 1;
//       if (results) results.hidden = true;
//       if (resultsContent) resultsContent.replaceChildren();
//     }

//     function resetModels() {
//       modelSelect.replaceChildren(new Option('Select Model', ''));
//       modelSelect.disabled = true;
//       modelIndex = -1;
//     }

//     function renderModels(product) {
//       resetModels();
//       if (!product) return;

//       modelIndex = findModelOptionIndex(product);

//       if (modelIndex === -1) {
//         setMessage(
//           'Model variant option was not found for this product. ' +
//           'Please check the product variant option names.'
//         );
//         return;
//       }

//       const modelMap = new Map();

//       (product.variants || []).forEach((variant) => {
//         const model = variant.options && variant.options[modelIndex];
//         if (!model) return;
//         modelMap.set(
//           model,
//           Boolean(modelMap.get(model)) || Boolean(variant.available)
//         );
//       });

//       const models = [...modelMap.keys()].sort(collator.compare);

//       models.forEach((model) => {
//         const inStock = modelMap.get(model);
//         const option = new Option(
//           inStock ? model : `${model} (Sold out)`,
//           model
//         );
//         option.disabled = !inStock;
//         modelSelect.add(option);
//       });

//       modelSelect.disabled = models.length === 0;

//       setMessage(
//         models.length === 0 ? 'No models are available for this brand.' : ''
//       );
//     }

//     function pickVariant(product, model) {
//       const matches = (product.variants || []).filter(
//         (variant) => variant.options && variant.options[modelIndex] === model
//       );

//       return matches.find((variant) => variant.available) || matches[0] || null;
//     }

//     async function fetchProductDetail(product) {
//       try {
//         const response = await fetch(
//           `${root}products/${encodeURIComponent(product.handle)}.js`,
//           { headers: { Accept: 'application/json' } }
//         );

//         if (!response.ok) return null;

//         return await response.json();
//       } catch (error) {
//         console.warn('[vehicle-finder] Product details fetch failed', error);
//         return null;
//       }
//     }

//     // Adds one item to the cart. When an image is attached, the request is sent as
//     // multipart form data so Shopify stores the file and saves its URL as a line item property.
//     async function addToCart(variantId, quantity, properties, file) {
//       let response;

//       if (file) {
//         const formData = new FormData();
//         formData.append('id', String(variantId));
//         formData.append('quantity', String(quantity));

//         Object.entries(properties).forEach(([key, value]) => {
//           formData.append(`properties[${key}]`, value);
//         });

//         formData.append(`properties[${uploadLabel}]`, file, file.name);

//         response = await fetch(`${root}cart/add.js`, {
//           method: 'POST',
//           headers: { Accept: 'application/json' },
//           body: formData
//         });
//       } else {
//         const item = { id: variantId, quantity };

//         if (Object.keys(properties).length) item.properties = properties;

//         response = await fetch(`${root}cart/add.js`, {
//           method: 'POST',
//           headers: {
//             'Content-Type': 'application/json',
//             Accept: 'application/json'
//           },
//           body: JSON.stringify({ items: [item] })
//         });
//       }

//       const data = await response.json().catch(() => ({}));

//       if (!response.ok) {
//         throw new Error(
//           data.description ||
//           data.message ||
//           'Could not add this product to the cart.'
//         );
//       }

//       return data;
//     }

//     // Clones the static icon boxes rendered by Liquid (section blocks)
//     function buildIconBox() {
//       const template = section.querySelector('template[data-finder-icon-box]');
//       if (!template) return null;

//       const fragment = template.content.cloneNode(true);

//       // Safety: never run scripts that might be pasted into a custom SVG field
//       fragment.querySelectorAll('script').forEach((node) => node.remove());

//       return fragment;
//     }

//     // Builds the Description / Key Features / FAQs / Shipping & Returns accordion
//     function buildAccordion(product, detail) {
//       const rows = [];

//       const description =
//         detail && typeof detail.description === 'string'
//           ? detail.description.trim()
//           : '';

//       if (description) {
//         const body = el('div', 'vf-acc__rich');
//         setSafeHtml(body, normalizeDescription(description));
//         rows.push({ title: 'DESCRIPTION', body, open: true });
//       }

//       const features = splitLines(product.key_features);

//       if (features.length) {
//         const list = el('ul', 'vf-acc__features');
//         features.forEach((line) => list.append(el('li', null, line)));
//         rows.push({ title: 'KEY FEATURES', body: list, open: false });
//       }

//       const faqLines = splitLines(product.faqs);

//       if (faqLines.length) {
//         const body = el('div', 'vf-acc__text');

//         faqLines.forEach((line, index) => {
//           if (index > 0) body.append(document.createElement('br'));
//           body.append(document.createTextNode(line));
//         });

//         rows.push({ title: 'FAQs', body, open: false });
//       }

//       const shippingLines = splitLines(product.shipping_returns);

//       if (shippingLines.length) {
//         const body = el('div', 'vf-acc__rich');
//         shippingLines.forEach((line) => body.append(el('p', null, line)));
//         rows.push({ title: 'SHIPPING & RETURNS', body, open: false });
//       } else if (shippingPolicy.trim()) {
//         const body = el('div', 'vf-acc__rich');
//         setSafeHtml(body, shippingPolicy);
//         rows.push({ title: 'SHIPPING & RETURNS', body, open: false });
//       }

//       if (!rows.length) return null;

//       const accordion = el('div', 'vf-acc');

//       rows.forEach((row, index) => {
//         const bodyId = `${section.id}-acc-${index}`;
//         const rowElement = el(
//           'div',
//           row.open ? 'vf-acc__row is-open' : 'vf-acc__row'
//         );

//         const header = el('button', 'vf-acc__header');
//         header.type = 'button';
//         header.setAttribute('aria-expanded', String(row.open));
//         header.setAttribute('aria-controls', bodyId);
//         header.append(
//           el('span', null, row.title),
//           el('span', 'vf-acc__toggle', '+')
//         );

//         const bodyWrap = el('div', 'vf-acc__body');
//         bodyWrap.id = bodyId;
//         bodyWrap.inert = !row.open;

//         const inner = el('div', 'vf-acc__inner');
//         const content = el('div', 'vf-acc__content');
//         content.append(row.body);
//         inner.append(content);
//         bodyWrap.append(inner);

//         header.addEventListener('click', () => {
//           const isOpen = rowElement.classList.toggle('is-open');
//           header.setAttribute('aria-expanded', String(isOpen));
//           bodyWrap.inert = !isOpen;
//         });

//         rowElement.append(header, bodyWrap);
//         accordion.append(rowElement);
//       });

//       return accordion;
//     }

//     function renderDetail(product, variant, detail, trim, year) {
//       const uid = `${section.id}-detail`;
//       const modelName = modelSelect.value;
//       const title = (detail && detail.title) || product.title;

//       const wrap = el('div', 'vf-detail');

//       /* ----- Gallery ----- */
//       // Only the image of the selected model (variant) is shown, not the whole brand gallery
//       let images = [];

//       const detailVariant =
//         detail &&
//         (detail.variants || []).find((item) => item.id === variant.id);

//       const variantImage =
//         (detailVariant &&
//           detailVariant.featured_image &&
//           detailVariant.featured_image.src) ||
//         variant.image;

//       if (variantImage) {
//         images = [variantImage];
//       } else {
//         // The model has no image of its own, so fall back to the brand's main image only
//         const fallbackImage = (detail && detail.featured_image) || product.image;
//         if (fallbackImage) images = [fallbackImage];
//       }

//       const gallery = el('div', 'vf-gallery');
//       const thumbs = el('div', 'vf-thumbs');
//       const stage = el('div', 'vf-stage');
//       const mainImage = document.createElement('img');
//       const thumbButtons = [];
//       let current = 0;

//       mainImage.className = 'vf-stage__image';
//       mainImage.alt = `${title} ${modelName}`;
//       mainImage.width = 900;
//       mainImage.height = 900;

//       function showImage(index) {
//         if (!images.length) return;
//         current = (index + images.length) % images.length;
//         mainImage.src = sizedImage(images[current], 900);
//         thumbButtons.forEach((button, i) => {
//           button.classList.toggle('is-active', i === current);
//           button.setAttribute('aria-current', i === current ? 'true' : 'false');
//         });
//       }

//       if (images.length) {
//         stage.append(mainImage);
//       } else {
//         stage.classList.add('vf-stage--empty');
//       }

//       if (images.length > 1) {
//         images.forEach((src, index) => {
//           const button = el('button', 'vf-thumb');
//           button.type = 'button';
//           button.setAttribute('aria-label', `Show image ${index + 1}`);

//           const img = document.createElement('img');
//           img.src = sizedImage(src, 160);
//           img.alt = '';
//           img.loading = 'lazy';
//           button.append(img);

//           button.addEventListener('click', () => showImage(index));
//           thumbs.append(button);
//           thumbButtons.push(button);
//         });

//         const prev = el('button', 'vf-arrow vf-arrow--prev', '‹');
//         const next = el('button', 'vf-arrow vf-arrow--next', '›');

//         prev.type = 'button';
//         next.type = 'button';
//         prev.setAttribute('aria-label', 'Previous image');
//         next.setAttribute('aria-label', 'Next image');

//         prev.addEventListener('click', () => showImage(current - 1));
//         next.addEventListener('click', () => showImage(current + 1));

//         stage.append(prev, next);
//         gallery.append(thumbs);
//       }

//       gallery.append(stage);
//       showImage(0);

//       if (images.length) {
//         const openPreview = () =>
//           openLightbox(images, current, mainImage.alt, mainImage);

//         mainImage.classList.add('is-zoomable');
//         mainImage.tabIndex = 0;
//         mainImage.setAttribute('role', 'button');
//         mainImage.setAttribute('aria-label', 'Open image preview');

//         mainImage.addEventListener('click', openPreview);
//         mainImage.addEventListener('keydown', (event) => {
//           if (event.key === 'Enter' || event.key === ' ') {
//             event.preventDefault();
//             openPreview();
//           }
//         });
//       }

//       /* ----- Info ----- */
//       const info = el('div', 'vf-info');

//       const titleHasModel = normalize(title).endsWith(normalize(modelName));
//       info.append(
//         el('h4', 'vf-title', titleHasModel ? title : `${title} ${modelName}`.trim())
//       );

//       const priceRow = el('div', 'vf-price-row');
//       priceRow.append(el('span', 'vf-price', variant.price));

//       if (variant.compare_at) {
//         priceRow.append(el('span', 'vf-compare', variant.compare_at));

//         if (variant.compare_at_cents > variant.price_cents) {
//           const percent = Math.round(
//             ((variant.compare_at_cents - variant.price_cents) /
//               variant.compare_at_cents) *
//               100
//           );
//           priceRow.append(el('span', 'vf-badge', `${percent}% OFF`));
//         }
//       }

//       info.append(priceRow);

//       const points = extractPoints(detail && detail.description);

//       if (points.length) {
//         const list = el('ul', 'vf-points');
//         points.forEach((point) => list.append(el('li', null, point)));
//         info.append(list);
//       }

//       if (trustPoints.length) {
//         const trust = el('ul', 'vf-trust');
//         trustPoints.forEach((point) => trust.append(el('li', null, point)));
//         info.append(trust);
//       }

//       /* ----- Two input boxes (Trim, Year) ----- */
//       const fields = el('div', 'vf-fields');

//       function buildField(labelText, id, attrs) {
//         const field = el('div', 'vehicle-finder__field');
//         const label = el('label', null, `${labelText} `);
//         label.htmlFor = id;

//         // Required marker, same style as the Brand and Model labels
//         const marker = el('span', null, '*');
//         marker.setAttribute('aria-hidden', 'true');
//         label.append(marker);

//         const input = document.createElement('input');
//         input.id = id;
//         input.required = true;
//         input.setAttribute('aria-required', 'true');
//         Object.entries(attrs).forEach(([key, value]) =>
//           input.setAttribute(key, value)
//         );

//         // Remove the error highlight as soon as the user starts typing
//         input.addEventListener('input', () => {
//           input.removeAttribute('aria-invalid');
//         });

//         field.append(label, input);
//         return { field, input };
//       }

//       const trimField = buildField('Trim', `${uid}-trim`, {
//         type: 'text',
//         placeholder: 'Enter Trim',
//         autocomplete: 'off'
//       });

//       const yearField = buildField('Year', `${uid}-year`, {
//         type: 'number',
//         inputmode: 'numeric',
//         min: '1900',
//         max: '2100',
//         step: '1',
//         placeholder: 'Enter Year'
//       });

//       trimField.input.value = trim || '';
//       yearField.input.value = year || '';

//       fields.append(trimField.field, yearField.field);

//       /* ----- Optional image upload ----- */
//       let selectedFile = null;
//       let previewUrl = '';

//       if (uploadEnabled) {
//         const uploadId = `${uid}-upload`;
//         const uploadField = el('div', 'vehicle-finder__field vf-upload');

//         const uploadLabelElement = el('label', null, `${uploadLabel} `);
//         uploadLabelElement.htmlFor = uploadId;
//         uploadLabelElement.append(el('span', 'vf-upload__optional', '(optional)'));

//         const fileInput = document.createElement('input');
//         fileInput.id = uploadId;
//         fileInput.type = 'file';
//         fileInput.accept = 'image/*';
//         fileInput.className = 'vf-upload__input';

//         const hint = el(
//           'p',
//           'vf-upload__hint',
//           'Image files only (JPG, PNG or WEBP), up to 10 MB.'
//         );

//         const preview = el('div', 'vf-upload__preview');
//         preview.hidden = true;

//         const thumb = document.createElement('img');
//         thumb.className = 'vf-upload__thumb';
//         thumb.alt = '';

//         const fileName = el('span', 'vf-upload__name');
//         const removeButton = el('button', 'vf-upload__remove', 'Remove');
//         removeButton.type = 'button';
//         removeButton.setAttribute('aria-label', 'Remove the selected image');

//         preview.append(thumb, fileName, removeButton);

//         // Resets the selected file and its preview
//         function clearFile() {
//           selectedFile = null;

//           if (previewUrl) {
//             URL.revokeObjectURL(previewUrl);
//             previewUrl = '';
//           }

//           fileInput.value = '';
//           preview.hidden = true;
//           thumb.removeAttribute('src');
//           fileName.textContent = '';
//         }

//         fileInput.addEventListener('change', () => {
//           const file = fileInput.files && fileInput.files[0];

//           clearFile();

//           if (!file) return;

//           if (!file.type || !file.type.startsWith('image/')) {
//             setStatus('Please choose an image file (JPG, PNG or WEBP).', 'error');
//             return;
//           }

//           if (file.size > MAX_UPLOAD_BYTES) {
//             setStatus('The image is too large. The maximum size is 10 MB.', 'error');
//             return;
//           }

//           setStatus('');

//           selectedFile = file;
//           previewUrl = URL.createObjectURL(file);
//           thumb.src = previewUrl;
//           fileName.textContent = file.name;
//           preview.hidden = false;
//         });

//         removeButton.addEventListener('click', clearFile);

//         uploadField.append(uploadLabelElement, fileInput, hint, preview);
//         fields.append(uploadField);
//       }

//       info.append(fields);

//       /* ----- Quantity + buttons ----- */
//       const actions = el('div', 'vf-actions');

//       const qty = el('div', 'vf-qty');
//       const minus = el('button', 'vf-qty__btn', '−');
//       const plus = el('button', 'vf-qty__btn', '+');
//       const qtyInput = document.createElement('input');

//       minus.type = 'button';
//       plus.type = 'button';
//       minus.setAttribute('aria-label', 'Decrease quantity');
//       plus.setAttribute('aria-label', 'Increase quantity');

//       qtyInput.type = 'number';
//       qtyInput.min = '1';
//       qtyInput.step = '1';
//       qtyInput.value = '1';
//       qtyInput.inputMode = 'numeric';
//       qtyInput.setAttribute('aria-label', 'Quantity');

//       function getQuantity() {
//         const value = parseInt(qtyInput.value, 10);
//         return Number.isInteger(value) && value > 0 ? value : 1;
//       }

//       minus.addEventListener('click', () => {
//         qtyInput.value = String(Math.max(1, getQuantity() - 1));
//       });

//       plus.addEventListener('click', () => {
//         qtyInput.value = String(getQuantity() + 1);
//       });

//       qtyInput.addEventListener('change', () => {
//         qtyInput.value = String(getQuantity());
//       });

//       qty.append(minus, qtyInput, plus);

//       const addButton = el('button', 'vf-btn vf-btn--add', addLabel);
//       const buyButton = el('button', 'vf-btn vf-btn--buy', buyLabel);

//       addButton.type = 'button';
//       buyButton.type = 'button';

//       if (!variant.available) {
//         addButton.textContent = 'Sold out';
//         addButton.disabled = true;
//         buyButton.disabled = true;
//         qtyInput.disabled = true;
//         minus.disabled = true;
//         plus.disabled = true;
//       }

//       actions.append(qty, addButton, buyButton);
//       info.append(actions);

//       const status = el('p', 'vf-status');
//       status.setAttribute('role', 'status');
//       status.setAttribute('aria-live', 'polite');
//       status.hidden = true;
//       info.append(status);

//       // const fullLink = el('a', 'vf-full-link', 'View full product page');
//       // fullLink.href = variant.url || `${product.url}?variant=${variant.id}`;
//       // info.append(fullLink);

//       function setStatus(text, type) {
//         status.textContent = '';
//         status.className = 'vf-status';
//         status.hidden = !text;

//         if (!text) return;

//         if (type) status.classList.add(`vf-status--${type}`);
//         status.append(document.createTextNode(text));

//         if (type === 'success') {
//           const link = el('a', 'vf-status__link', 'View cart');
//           link.href = cartUrl;
//           status.append(' ', link);
//         }
//       }

//       // Trim and Year are mandatory before adding to cart or buying
//       function collectProperties() {
//         const trimValue = trimField.input.value.trim();
//         const yearValue = yearField.input.value.trim();

//         trimField.input.removeAttribute('aria-invalid');
//         yearField.input.removeAttribute('aria-invalid');

//         const missing = [];

//         if (!trimValue) {
//           missing.push('Trim');
//           trimField.input.setAttribute('aria-invalid', 'true');
//         }

//         if (!yearValue) {
//           missing.push('Year');
//           yearField.input.setAttribute('aria-invalid', 'true');
//         }

//         if (missing.length) {
//           setStatus(
//             `Please enter ${missing.join(' and ')} before continuing.`,
//             'error'
//           );

//           (trimValue ? yearField : trimField).input.focus();
//           return null;
//         }

//         const number = Number(yearValue);

//         if (!Number.isInteger(number) || number < 1900 || number > 2100) {
//           yearField.input.setAttribute('aria-invalid', 'true');
//           setStatus('Please enter a valid year between 1900 and 2100.', 'error');
//           yearField.input.focus();
//           return null;
//         }

//         return { Trim: trimValue, Year: String(number) };
//       }

//       let busy = false;

//       function setBusy(value) {
//         busy = value;
//         addButton.disabled = value || !variant.available;
//         buyButton.disabled = value || !variant.available;
//       }

//       addButton.addEventListener('click', async () => {
//         if (busy) return;

//         const properties = collectProperties();
//         if (!properties) return;

//         setBusy(true);
//         setStatus('');

//         try {
//           await addToCart(variant.id, getQuantity(), properties, selectedFile);
//           setStatus('Added to your cart.', 'success');

//           // Generic events so a theme cart drawer / cart count can refresh
//           ['cart:refresh', 'cart:updated'].forEach((name) =>
//             document.dispatchEvent(new CustomEvent(name, { bubbles: true }))
//           );
//         } catch (error) {
//           setStatus(error.message, 'error');
//         } finally {
//           setBusy(false);
//         }
//       });

//       buyButton.addEventListener('click', async () => {
//         if (busy) return;

//         const properties = collectProperties();
//         if (!properties) return;

//         setBusy(true);
//         setStatus('');

//         try {
//           await addToCart(variant.id, getQuantity(), properties, selectedFile);
//           window.location.href = checkoutUrl;
//         } catch (error) {
//           setStatus(error.message, 'error');
//           setBusy(false);
//         }
//       });

//       wrap.append(gallery, info);

//       const fragment = document.createDocumentFragment();
//       fragment.append(wrap);

//       const iconBox = buildIconBox();
//       if (iconBox) fragment.append(iconBox);

//       const accordion = showDetails ? buildAccordion(product, detail) : null;
//       if (accordion) fragment.append(accordion);

//       resultsContent.replaceChildren(fragment);
//       results.hidden = false;
//     }

//     async function showProduct(product, variant, trim, year) {
//       const currentRequest = ++requestId;

//       results.hidden = false;
//       resultsContent.replaceChildren(
//         el('p', 'vf-loading', 'Loading product details...')
//       );

//       const detail = await fetchProductDetail(product);

//       // Skip rendering if the user searched or changed a selection in the meantime
//       if (currentRequest !== requestId) return;

//       renderDetail(product, variant, detail, trim, year);
//       results.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
//     }

//     function initialize() {
//       const productsElement = section.querySelector('[data-finder-products]');

//       if (!productsElement) {
//         throw new Error('Product data script was not found.');
//       }

//       products = JSON.parse(productsElement.textContent);

//       brandSelect.replaceChildren(new Option('Select Brand', ''));

//       products
//         .slice()
//         .sort((a, b) => collator.compare(a.title, b.title))
//         .forEach((product) => {
//           brandSelect.add(new Option(product.title, String(product.id)));
//         });

//       brandSelect.disabled = products.length === 0;

//       setMessage(
//         products.length === 0
//           ? 'No products were found in this collection. ' +
//             'Select a collection in the section settings if this is not a collection page.'
//           : ''
//       );
//     }

//     brandSelect.addEventListener('change', () => {
//       const productId = String(brandSelect.value).trim();

//       clearResults();

//       selectedProduct =
//         products.find((product) => String(product.id) === productId) || null;

//       if (!selectedProduct) {
//         resetModels();
//         setMessage('');
//         return;
//       }

//       setMessage('');
//       renderModels(selectedProduct);
//     });

//     // Validates the current selection and shows the matching product
//     function runSearch() {
//       if (!selectedProduct || !modelSelect.value) {
//         clearResults();
//         setMessage('Please select both Brand and Model.');
//         return;
//       }

//       const trim = trimInput ? trimInput.value.trim() : '';
//       const yearRaw = yearInput ? yearInput.value.trim() : '';

//       if (yearRaw) {
//         const year = Number(yearRaw);

//         if (!Number.isInteger(year) || year < 1900 || year > 2100) {
//           clearResults();
//           setMessage('Please enter a valid year between 1900 and 2100.');
//           return;
//         }
//       }

//       const variant = pickVariant(selectedProduct, modelSelect.value);

//       if (!variant) {
//         clearResults();
//         setMessage('No product found for this model. Please try another one.');
//         return;
//       }

//       setMessage('');
//       showProduct(selectedProduct, variant, trim, yearRaw);
//     }

//     // The product details appear automatically as soon as a model is selected
//     modelSelect.addEventListener('change', () => {
//       clearResults();
//       setMessage('');

//       if (modelSelect.value) runSearch();
//     });

//     // The search button still works as a manual fallback
//     form.addEventListener('submit', (event) => {
//       event.preventDefault();
//       runSearch();
//     });

//     try {
//       initialize();
//     } catch (error) {
//       console.error('[vehicle-finder]', error);
//       brandSelect.replaceChildren(new Option('Unable to load brands', ''));
//       brandSelect.disabled = true;
//       setMessage('Products could not be loaded. Please refresh and try again.');
//     }
//   }

//   function initializeAllFinders(root = document) {
//     root.querySelectorAll('[data-vehicle-finder]').forEach(initializeFinder);
//   }

//   if (document.readyState === 'loading') {
//     document.addEventListener('DOMContentLoaded', () => initializeAllFinders());
//   } else {
//     initializeAllFinders();
//   }

//   document.addEventListener('shopify:section:load', (event) => {
//     if (event.target && event.target.querySelectorAll) {
//       initializeAllFinders(event.target);
//     }
//   });
// })();